import { makeAutoObservable } from 'mobx';
import ErrorUtil from '../error/ErrorUtil';
import RootStore from '../stores/RootStore';
import SsoStore from '../stores/SsoStore';
import API, { CommonAPI } from '../util/ApiUtil';
import { FetchStatus } from '../util/FetchStatus';

export interface CustomerProductData {
  productOfferingCode: string;
  productCategoryCodes: string[];
  productOfferingName: string;
}

export interface ServiceCountWithType {
  serviceCount: number;
  type?: string;
  mainServiceId?: number;
}

class ProductStore {
  public customerProducts: CustomerProductData[] = [];
  public customerProductsStatus: FetchStatus = FetchStatus.UNINITIALIZED;
  public serviceCountsFetchStatus: FetchStatus = FetchStatus.UNINITIALIZED;
  public serviceCounts: Map<string, ServiceCountWithType> = new Map<string, ServiceCountWithType>();
  public lastCustomerId: number;
  private ssoStore: SsoStore;

  constructor(rootStore: RootStore) {
    makeAutoObservable<ProductStore, 'ssoStore'>(this, { ssoStore: false }, { autoBind: true });
    this.ssoStore = rootStore.ssoStore;
  }

  public fetchServiceCounts = async (serviceType: string) => {
    if (!this.ssoStore.loggedIn) {
      this.setServicesState(serviceType, null, FetchStatus.FETCHING);
      return Promise.resolve();
    }
    this.setServicesState(serviceType, null, FetchStatus.FETCHING);

    try {
      const response = await CommonAPI.get(`/product/number-of-services/${serviceType}`);
      this.setServicesState(serviceType, response.data, FetchStatus.FETCHED);
    } catch (e) {
      ErrorUtil.pushErrorToTeliaRumAndLog(e as Error);
      this.setServicesState(serviceType, null, FetchStatus.ERROR);
    }
  };

  public checkCustomerProducts = async (categoryCodes?: string[], offeringCodes?: string[], offeringName?: string, origins?: string[]) => {
    this.setCustomerProductsState(null, FetchStatus.FETCHING);
    if (!this.ssoStore.customerId) {
      this.setCustomerProductsState([], null);
      return Promise.resolve();
    }
    if (this.ssoStore.customerId !== this.lastCustomerId) {
      this.setCustomerProductsState([], null);
    }
    if (this.shouldNotFetch(categoryCodes, offeringCodes, offeringName)) {
      this.setCustomerProductsState(null, FetchStatus.FETCHED);
      return Promise.resolve();
    }
    try {
      const response = await API.get('/product/customer/search', {
        params: {
          productOfferingCodes: offeringCodes,
          productCategoryCodes: categoryCodes,
          productOfferingName: offeringName,
          origins,
        },
      });
      this.setPreviousCustomerId(this.ssoStore.customerId);
      this.setCustomerProductsState(this.customerProducts.concat(response.data), FetchStatus.FETCHED);
    } catch (e) {
      ErrorUtil.pushErrorToTeliaRumAndLog(e);
      this.setCustomerProductsState([], FetchStatus.ERROR);
    }
  };

  private shouldNotFetch = (categoryCodes: string[], offeringCodes: string[], offeringName: string) => {
    return (
      categoryCodes.every((cat) => this.customerProducts.some((cp) => cp.productCategoryCodes.includes(cat))) &&
      offeringCodes.every((offer) => this.customerProducts.some((cp) => cp.productOfferingCode === offer)) &&
      (!offeringName || this.customerProducts.some((cp) => cp.productOfferingName === offeringName))
    );
  };

  private setServicesState(serviceType: string, count: ServiceCountWithType, status: FetchStatus) {
    this.serviceCounts.set(serviceType, count);
    this.serviceCountsFetchStatus = status;
  }

  private setCustomerProductsState(data: CustomerProductData[], status: FetchStatus) {
    if (status !== null) {
      this.customerProductsStatus = status;
    }
    if (data !== null) {
      this.customerProducts = data;
    }
  }

  private setPreviousCustomerId(id: number) {
    this.lastCustomerId = id;
  }
}

export default ProductStore;
